import React, { createContext, useState, useContext, useEffect } from "react";
import moment from "moment";

const PosContext = createContext();

export const PosProvider = ({ children }) => {
  const apiUrl = process.env.REACT_APP_API_URL; // Base API URL from environment variable

  const [tables, setTables] = useState([
    { id: "Table101", area: "Restaurant 1", status: "Available", waiter: null, kots: [] },
    { id: "Table102", area: "Outdoor Area", status: "Available", waiter: null, kots: [] },
  ]);

  const [rooms, setRooms] = useState([]); // Initialize as empty array
  const [waiters] = useState(["Waiter 1", "Waiter 2", "Waiter 3"]);
  const [loading, setLoading] = useState(true); // Track loading state
  const [error, setError] = useState(null); // Track errors

  // Fetch occupied rooms from the API
  const fetchRooms = async () => {
    const token = localStorage.getItem("token"); // Retrieve token from localStorage

    if (!token) {
      setError("No authentication token found");
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const response = await fetch(`${apiUrl}/rooms/occupied-rooms`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error("Failed to fetch occupied rooms");
      }

      const data = await response.json();
      console.log("Fetched rooms:", data);
      setRooms(data); // Set the fetched rooms data
      setError(null); // Clear any previous errors
    } catch (err) {
      console.error("Error fetching rooms:", err);
      setError(err.message);
      // Fallback data in case of error
      setRooms([
        { id: "Room 101", status: "Available", kots: [] },
        { id: "Room 102", status: "Available", kots: [] },
      ]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch rooms when the component mounts
  useEffect(() => {
    fetchRooms();
  }, []); // Empty dependency array to run only on mount

  const assignTable = (tableId, waiter) => {
    setTables((prev) =>
      prev.map((table) =>
        table.id === tableId ? { ...table, status: "Occupied", waiter } : table
      )
    );
  };

  const assignRoom = (roomId) => {
    setRooms((prev) =>
      prev.map((room) =>
        room.id === roomId ? { ...room, status: "Occupied" } : room
      )
    );
  };

  const addKOT = (tableId, roomId, kot) => {
    if (tableId) {
      setTables((prev) =>
        prev.map((table) =>
          table.id === tableId ? { ...table, kots: [...table.kots, kot] } : table
        )
      );
    } else if (roomId) {
      setRooms((prev) =>
        prev.map((room) =>
          room.id === roomId ? { ...room, kots: [...room.kots, kot] } : room
        )
      );
    }
  };

  const settleTable = (tableId) => {
    setTables((prev) =>
      prev.map((table) =>
        table.id === tableId ? { ...table, status: "Available", waiter: null, kots: [] } : table
      )
    );
  };

  const settleRoom = (roomId) => {
    setRooms((prev) =>
      prev.map((room) =>
        room.id === roomId ? { ...room, kots: [], status: "Available" } : room
      )
    );
  };

  const value = {
    tables,
    rooms,
    waiters,
    assignTable,
    assignRoom,
    addKOT,
    settleTable,
    settleRoom,
    setRooms,
    fetchRooms, // Expose fetchRooms method
    loading,
    error,
  };

  return <PosContext.Provider value={value}>{children}</PosContext.Provider>;
};

export const usePosContext = () => {
  const context = useContext(PosContext);
  if (!context) {
    throw new Error("usePosContext must be used within a PosProvider");
  }
  return context;
};